import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadArtFile, artUrl } from "@/lib/uploadArt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookPlus, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Small reusable action to capture the current tab's screenshots + notes into the Print Studio manual.
export const SaveToManualButton = ({ tab }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);

  const addImages = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const up = await uploadArtFile(f);
        setImages((prev) => [...prev, { file_id: up.file_id, name: f.name }]);
      }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!title.trim() && !note.trim() && images.length === 0) { toast.error("Add a title, note or image"); return; }
    setBusy(true);
    try {
      await api.post("/studio/manual-notes", {
        tab, title: title.trim(), note: note.trim(),
        image_file_ids: images.map((i) => i.file_id),
      });
      toast.success("Saved to the manual");
      setOpen(false); setTitle(""); setNote(""); setImages([]);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="ghost" size="sm" className="text-slate-500 hover:text-[#2495D3]" onClick={() => setOpen(true)} data-testid={`save-to-manual-${tab}`}>
        <BookPlus size={15} className="mr-1.5" /> Save to Manual
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-head">Add to Print Studio Manual</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. How we set up a 24×48 banner" className="rounded-sm mt-1" data-testid="manual-note-title" />
            </div>
            <div>
              <Label className="text-xs">Note / steps</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Describe the steps or tip for your shop…" className="rounded-sm mt-1" data-testid="manual-note-text" />
            </div>
            <div>
              <Label className="text-xs">Screenshots / images</Label>
              <label className="mt-1 flex items-center justify-center gap-2 h-16 rounded-sm border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-xs text-slate-500">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Add images
                <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={(e) => { addImages([...e.target.files]); e.target.value = ""; }} data-testid="manual-note-images" />
              </label>
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {images.map((img, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-sm overflow-hidden border">
                      <img src={artUrl(img.file_id)} alt={img.name} className="w-full h-full object-cover" />
                      <button onClick={() => setImages(images.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-black/60 text-white p-0.5"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-sm" data-testid="manual-note-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SaveToManualButton;
